import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { findValueInEnum, maybe } from "@saleor/misc";
import { getStringOrPlaceholder } from "@saleor/misc";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import { ListViews } from "@saleor/types";
import { SmopStatusEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React, { useEffect, useState } from "react";

import SellerListPage from "../../components/SellerListPage";
import { useSellerListQuery } from "../../queries";
import {
  SellerAddPath,
  sellerDetailsUrl,
  sellerListUrl,
  SellerListUrlDialog,
  SellerListUrlQueryParams,
  sellerSortListUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterTabs,
  saveFilterTab
} from "./filters";
import {
  getFilterOpts,
  getFilterQueryParam,
  getFilterVariables
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface SellerListProp {
  params: SellerListUrlQueryParams;
}

const SellerList: React.FC<SellerListProp> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const shop = useShop();
  const { user } = useUser();
  const { updateListSettings, settings } = useListSettings(
    ListViews.BRANDS_LIST
  );
  const tabs = getFilterTabs();

  const paginationState = createPaginationState(settings.rowNumber, params);
  const currencySymbol = shop?.defaultCurrency || "USD";
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params),
      status: maybe(() => findValueInEnum(params.status, SmopStatusEnum))
    }),

    [params]
  );

  const { data: sellerQueryData, loading } = useSellerListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      filter: {
        ...queryVariables.filter,
        id: params.brand ? [params.brand] : null
      }
    }
  });
  React.useEffect(
    () =>
      navigate(
        sellerListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    sellerQueryData?.brandsPrivate.pageInfo,
    paginationState,
    params
  );

  const handleSort = createSortHandler(navigate, sellerSortListUrl, params);

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: sellerListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    SellerListUrlDialog,
    SellerListUrlQueryParams
  >(navigate, sellerListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      sellerListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(sellerListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const SellerData = sellerQueryData?.brandsPrivate?.edges.map(
    edge => edge.node
  );

  const filterOpts = getFilterOpts(params);

  // AUTOCOMPLETE SEARCH
  const brandInitialSearchData = {
    after: null,
    first: 20,
    query: ""
  };
  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: brandsQueryData
  } = useBrandPrivateSearch({
    variables: {
      ...brandInitialSearchData,
      accountManager: user.id
    }
  });
  const sellerBrandChoices = maybe(
    () => brandsQueryData?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  // const handleSubmit = async (data: SellerBrandFormData) => {
  //   navigate(sellerListUrl({ brand: data.sellerBrand }));
  // };

  const [brandLabel, setBrandLabel] = useState("");
  const getBrand = (brandID, choices) => {
    const getBrand = choices.find(brand => brand.id === brandID);
    if (getBrand) {
      setBrandLabel(getBrand.name + " - " + getBrand.legalName);
    }
  };

  useEffect(() => {
    if (sellerBrandChoices && params.brand) {
      getBrand(params.brand, sellerBrandChoices);
    }
  }, [sellerBrandChoices]);

  return (
    <>
      <SellerListPage
        onChangeBrand={brandID =>
          navigate(sellerListUrl({ ...params, brand: brandID }))
        }
        brands={sellerBrandChoices}
        brandID={params.brand}
        brandLabel={brandLabel}
        fetchBrands={query => searchBrands(query)}
        fetchMoreBrands={{
          hasMore: maybe(
            () => brandsQueryData.data.search.pageInfo.hasNextPage
          ),
          loading: brandsQueryData.loading,
          onFetchMore: loadMoreBrand
        }}
        sellers={SellerData}
        sort={getSortParams(params)}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        pageInfo={pageInfo}
        disabled={loading}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onSort={handleSort}
        onRowClick={id => () => navigate(sellerDetailsUrl(id))}
        onAdd={() => navigate(SellerAddPath)}
        // onSubmit={handleSubmit}
      />

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};

export default SellerList;
